import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { Link } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import {
	applyOffsets,
	getPaymentAmount,
	getPaymentExecutionDate,
} from "@msuite/utils/incoming-invoices";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import React from "react";
import { formatInvoiceAmount } from "@/components/incoming-invoices";
import { useCitadel } from "@/hooks/use-citadel";
import { useIncomingInvoiceOffsetCandidates } from "@/hooks/use-incoming-invoice-offset-candidates";
import { useHiddenProjectTabs } from "@/hooks/use-project-tabs";
import { PaymentOffsetsModal } from "@/modals/incoming-invoices/payment-offsets-modal";
import { projectTabPath } from "@/sites/projects/project/tabs";
import { IncomingInvoiceOffsetAmount } from "./incoming-invoice-offset-amount";

/** Props Interface */
interface IncomingInvoiceOffsetsProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

/**
 * The outgoing invoices planned to be taken off the payment ("Verrechnung"), each linked to its
 * project's invoices, with what it takes off and what is left to transfer — reckoned on today's
 * amount less the Skonto still open, as the export reckons it on its day. Shown while the invoice
 * is still headed for a payment file, when it carries a plan or its supplier has open outgoing
 * invoices to pick from; planning needs `update IncomingInvoice` and `update Invoice`, which the
 * routes ask for.
 */
export function IncomingInvoiceOffsets({ incomingInvoice }: IncomingInvoiceOffsetsProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const hiddenTabs = useHiddenProjectTabs();
	const pickerModal = useDisclosure();

	/** Query */
	const isAwaitingPayment =
		incomingInvoice?.state === "received" || incomingInvoice?.state === "approved";
	const canPlan =
		isAwaitingPayment &&
		!!incomingInvoice?.contactId &&
		citadel?.can("update", "IncomingInvoice") === true &&
		citadel.can("update", "Invoice");
	const { candidates } = useIncomingInvoiceOffsetCandidates(incomingInvoice?.id ?? "", canPlan);

	/** Computed */
	const planned = incomingInvoice?.plannedOffsets ?? [];
	const currency = incomingInvoice?.currency ?? "EUR";
	const amount = incomingInvoice
		? getPaymentAmount(incomingInvoice, true, getPaymentExecutionDate()).amount
		: null;
	const result = applyOffsets(amount ?? 0, planned);

	/** Guard */
	if (!incomingInvoice || !isAwaitingPayment || amount === null) return null;
	if (!planned.length && (!canPlan || !candidates.length)) return null;

	/** Render */
	return (
		<React.Fragment>
			<ContentCard
				title={t("incoming_invoices.payments.offsets.offsets")}
				icon={icon("ArrowLeftRight")}
				accessory={
					canPlan && (
						<Tooltip label={t("incoming_invoices.payments.offsets.offset")}>
							<Button
								size="xs"
								variant="ghost"
								icon={<Icon name="Edit" />}
								onClick={pickerModal.onOpen}
								data-cy="plan-incoming-invoice-offsets-button"
							/>
						</Tooltip>
					)
				}
			>
				{planned.length ? (
					<React.Fragment>
						<DataGrid>
							{planned.map((offset) => (
								<DataGrid.Row key={offset.id}>
									<DataGrid.Label>
										{t("incoming_invoices.payments.offsets.less_offset")}
									</DataGrid.Label>
									<DataGrid.Value
										append={
											<IncomingInvoiceOffsetAmount
												outgoingInvoiceId={offset.id}
												result={result}
												currency={currency}
											/>
										}
									>
										<Link href={projectTabPath(offset.projectId, "invoices", hiddenTabs)}>
											{offset.number}
										</Link>
									</DataGrid.Value>
								</DataGrid.Row>
							))}
							<DataGrid.Row>
								<DataGrid.Label>
									{t("incoming_invoices.payments.offsets.to_transfer")}
								</DataGrid.Label>
								<DataGrid.Value>
									{result.remaining === 0
										? t("incoming_invoices.payments.offsets.offset_only")
										: formatInvoiceAmount(result.remaining, currency)}
								</DataGrid.Value>
							</DataGrid.Row>
						</DataGrid>
						<p className="label">{t("incoming_invoices.payments.offsets.planned_hint")}</p>
					</React.Fragment>
				) : (
					<p className="label">
						{t("incoming_invoices.payments.offsets.none_planned", {
							count: `${candidates.length}`,
						})}
					</p>
				)}
			</ContentCard>
			{pickerModal.isOpen && (
				<PaymentOffsetsModal
					isOpen={pickerModal.isOpen}
					onClose={pickerModal.onClose}
					incomingInvoiceId={incomingInvoice.id}
					number={incomingInvoice.number}
					supplier={incomingInvoice.contact?.name ?? incomingInvoice.issuerName}
					amount={amount}
					plannedIds={planned.map((offset) => offset.id)}
				/>
			)}
		</React.Fragment>
	);
}
